/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { matchPath, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import cx from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';

import PDPHeader, { PDP_HEADER_TAB_LABEL } from '@zg-rentals/zrm-utils-js/lib/PDPHeader';
import ZRMUtils from '@zg-rentals/zrm-utils-js/lib/utils';
import {
    Alert,
    PageBreadcrumb,
    Spacer,
    Text,
    TextButton,
} from '@zillow/constellation';
import {
    Container,
    Col,
    Row,
} from '@zillow/zrs-comet';

import appLogger from 'app/shared/loggers/appLogger';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';

// Actions
import ModalActions from 'app/shared/actions/ModalActions';
import PostingActions from 'app/shared/actions/PostingActions';
import StaticMapActions from 'app/shared/actions/StaticMapActions';

// Components
import { AddPropertyModal } from 'app/shared/components/Modal';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';

import SpinnerContainer from 'app/shared/components/SpinnerContainer';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import EditListingPage from 'app/shared/containers/EditListingPage/Loadable';
import StatisticsPage from 'app/shared/containers/StatisticsPage';
import ReviewsPage from 'app/shared/containers/ReviewsPage';

// Constants
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import StringConstants from 'app/shared/constants/StringConstants';

// model
import ErrorRMW from 'app/shared/model/ErrorRMW';

// Selectors
import {
    AppSelectors,
    ModalSelectors,
    PropertiesSelectors,
    RoutingSelectors,
} from 'app/shared/selectors';

// utils
import analyticsUtils from 'app/client/utils/analyticsUtils';

class PropertyDetailsPage extends React.Component {
    static contextType = RMWContext;

    static contextTypes = {
        brand: PropTypes.string,
        paths: PropTypes.object,
    }

    constructor(props) {
        super(props);

        // init state
        this.state = {
            isLoading: true,
            listing: {},
            skipEditStage: false,
            isEditListingFocused: false,
            errorRMWModel: ErrorRMW(),
        };

        this.getInitialTabIndex = this.getInitialTabIndex.bind(this);
        this.getSectionUrl = this.getSectionUrl.bind(this);
        this.onUnArchive = this.onUnArchive.bind(this);
        this.renderArchivedBanner = this.renderArchivedBanner.bind(this);
        this.renderBreadcrumb = this.renderBreadcrumb.bind(this);
        this.renderLoadComplete = this.renderLoadComplete.bind(this);
        this.renderPDP = this.renderPDP.bind(this);
        this.renderPreLoader = this.renderPreLoader.bind(this);
        this.showAddPropertyModal = this.showAddPropertyModal.bind(this);
        this.signUrl = this.signUrl.bind(this);
        this.trackPaymentsGetStartedClick = this.trackPaymentsGetStartedClick.bind(this);
        this.updateListing = this.updateListing.bind(this);
        this.updateSkipEditState = this.updateSkipEditState.bind(this);
        this.handleBreadcrumbClick = this.handleBreadcrumbClick.bind(this);
    }

    componentDidMount() {
        const initialTabName = this.getInitialTabIndex();
        const { propertyId } = this.props;

        if (initialTabName === 'contacts') {
            window.location.href = (`https://zillow.com/rental-manager/inbox/${propertyId}?byProperty=true&itc=ZRM_PDP_Contacts_Click`);
        }

        this.fetchPropertyDetails();
        window.scroll(0, 0);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !nextState.isLoading;
    }

    static getDerivedStateFromProps(props, state) {
        if (state === null) {
            return state;
        }

        const { intent } = ZRMUtils.queryUtils.parse(props.search);

        if (intent === 'listing') {
            if (!state.isEditListingFocused) {
                return { isEditListingFocused: true };
            }
        } else if (state.isEditListingFocused) {
            return { isEditListingFocused: false };
        }
        return null;
    }

    componentDidUpdate(prevProps) {
        const { paths } = this.context;
        const { location, modalAddProperty } = this.props;
        if (modalAddProperty.isOpen !== prevProps.modalAddProperty.isOpen) {
            if (!modalAddProperty.isOpen) {
                this.updateSkipEditState(false);
            }
        }

        if (location !== prevProps.location &&
            matchPath(location.pathname, { path: `${paths.propertiesPage}/:propertyId` })
        ) {
            // get propertyId from path
            const parts = this.props.location.pathname.split('/');
            const propertyId = parts[3];

            this.fetchPropertyDetails(propertyId);
        }
    }

    fetchPropertyDetails(propertyId = this.props.propertyId) {
        const { dispatch } = this.props;

        dispatch(PostingActions.property.getDetails(propertyId))
            .then((response) => {
                const { state } = response;

                analyticsUtils.setCustomDimension(
                    state,
                    AnalyticsConstants.GTM_CUSTOM_DIMENSION.STATE,
                    StringConstants.GA_TRACKERS.gtm
                );

                analyticsUtils.setCustomDimension(
                    state,
                    AnalyticsConstants.GTM_CUSTOM_DIMENSION.STATE,
                    StringConstants.GA_TRACKERS.dsp
                );

                this.setState({
                    isLoading: false,
                    listing: response,
                });
            })
            .catch((errorRMWModel) => {
                this.setState({
                    isLoading: false,
                    errorRMWModel,
                });
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'PropertyDetailsPage.fetchPropertyDetails',
                });
            });
    }

    getSectionUrl(section) {
        const { paths } = this.context;
        const baseDetailsUrl = paths.propertiesPage;
        return `${baseDetailsUrl}/:propertyId/${section}([?].*)?`;
    }

    showAddPropertyModal() {
        const { dispatch } = this.props;
        dispatch(ModalActions.updateDisplayAddPropertyModal({ isOpen: true }));
    }

    updateListing(listing) {
        this.setState({ listing });
    }

    updateSkipEditState(value = false) {
        this.setState({ skipEditStage: value });
    }

    /*
     * signUrl
     *
     * a function that is passed to the PDPHeader component where the static map will be set
     * based on the signed url response
     *
     * @return Promise
     */
    signUrl(url, brand) {
        const { dispatch } = this.props;
        const params = { brandName: brand };

        return dispatch(StaticMapActions.signUrl(url, params));
    }

    onUnArchive() {
        this.setState({ skipEditStage: true }, () => {
            this.showAddPropertyModal();
        });
    }

    trackPaymentsGetStartedClick() {
        const { dispatch } = this.props;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.PAYMENTS_GET_STARTED_CLICK));
    }

    /**
     * Render HTML partials
     *
     * renderBreadcrumb
     * renderLoadComplete
     * renderPDP
     */
    renderBreadcrumb() {
        const { paths } = this.context;
        const { propertiesFilter, prevPath, currentProperty } = this.props;
        // set this boolean to true if archived filter is true or in case user directly
        // comes on this page, make the breadcrumb to point to current property state (i.e archived/active & off market)
        const includeArchived = propertiesFilter.includeArchived ||
            (!prevPath && currentProperty.archived);

        const breadcrumbTitle = includeArchived ? 'Archived Properties' : 'My properties';
        const goToLink = includeArchived ? `${paths.propertiesPage}/?archived=true` : paths.propertiesPage;

        return (
            <PageBreadcrumb
                backButton={(
                    <TextButton
                        as={Link}
                        to={goToLink}
                    >
                        {breadcrumbTitle}
                    </TextButton>
                )}
            />
        );
    }

    renderErrorComponent() {
        const { errorRMWModel } = this.state;
        const { httpStatus } = errorRMWModel;
        const defaultErrorContent = 'Listing not found';

        const noPermissionsErrorContent = `This listing belongs to a different user & you do not have permission to access it.
            If you believe you should be able to access this listing,
            please make sure you are signed in to the same account that created this listing.`;

        const content = httpStatus === '403' ? noPermissionsErrorContent : defaultErrorContent;

        return (
            <Container className="PropertyDetailsPage-content">
                {this.renderBreadcrumb()}
                <Container className="PropertyDetailsPage-ErrorContent-Container">
                    <Row>
                        <Col>
                            <Text className="PropertyDetailsPage-ErrorContent" type="h4">{content}</Text>
                        </Col>
                    </Row>
                </Container>
            </Container>
        );
    }

    renderLoadComplete() {
        const { listing } = this.state;
        const hasListing = !isEmpty(listing);

        return <div>{hasListing ? this.renderPDP() : this.renderErrorComponent()}</div>;
    }

    renderArchivedBanner() {
        return (
            <Spacer marginBottom="md">
                <Alert
                    actionButton={<TextButton onClick={this.onUnArchive}>Unarchive</TextButton>}
                    body="This property is archived. Unarchive it to make edits."
                />
            </Spacer>
        );
    }

    getInitialTabIndex() {
        const { router } = this.props;

        // Expecting path to be [baseUrl]/rental-manager/properties/{propertyId}/*something*
        const tabName = router.location.pathname.split('/')[4];

        return tabName;
    }

    renderTabPanelComponent = (tabName) => {
        const { listing, isEditListingFocused } = this.state;
        const { currentProperty } = this.props;
        const { listingTitle, restrictions } = listing;
        const { isCommunity, isPaymentsRestricted } = restrictions;
        const { archived, street, unit } = currentProperty;

        const displayPaymentsTab = !isPaymentsRestricted;

        // community properties use title
        const address = isCommunity ? listingTitle : [street, unit].join(' ').trim();

        let component = null;

        switch (tabName) {
            case PDP_HEADER_TAB_LABEL.LISTING:
                component = (
                    <React.Fragment>
                        {archived && this.renderArchivedBanner()}
                        <EditListingPage
                            listing={listing}
                            address={address}
                            cityStateZip={
                                `${currentProperty.city}, ${currentProperty.state} ${currentProperty.zip}`
                            }
                            updateListing={this.updateListing}
                            isArchived={archived}
                            canAccessPayments={displayPaymentsTab}
                            isEditListingFocused={isEditListingFocused}
                            {...this.props}
                        />
                    </React.Fragment>
                );
                break;

            case PDP_HEADER_TAB_LABEL.LEASES:
                component = (
                    <React.Fragment>
                        {archived && this.renderArchivedBanner()}
                        <p>You will be automatically redirected shortly...</p>
                    </React.Fragment>
                );
                break;

            case PDP_HEADER_TAB_LABEL.ANALYTICS:
                component = (
                    <React.Fragment>
                        {archived && this.renderArchivedBanner()}
                        <StatisticsPage
                            hideAsideAnalytics={!listing.isCommunity}
                            {...this.props}
                        />
                    </React.Fragment>
                );
                break;

            case PDP_HEADER_TAB_LABEL.REVIEWS:
                component = (
                    <React.Fragment>
                        {archived && this.renderArchivedBanner()}
                        <ReviewsPage {...this.props} />
                    </React.Fragment>
                );
                break;

            default:
                console.info('No case for: ', tabName);
                break;
        }

        return component;
    }

    handleBreadcrumbClick(event) {
        event.preventDefault();
        const { currentTarget } = event;
        const host = `${window.document.location.protocol}//${window.document.location.host}`;
        const path = currentTarget.href.replace(host, '');

        window.router.transitionTo(path);
    }

    renderPDP() {
        const { brand, isInMobileWebView, paths } = this.context;
        const { listing, isEditListingFocused } = this.state;

        const { base } = paths;

        const tabConfig = {
            [PDP_HEADER_TAB_LABEL.LISTING]: {
                tabComponent: this.renderTabPanelComponent(PDP_HEADER_TAB_LABEL.LISTING),
            },
            [PDP_HEADER_TAB_LABEL.MESSAGES]: {
                tabPath: `${base}/inbox/${listing.propertyId}?byProperty=true&itc=ZRM_PDP_Contacts_Click`,
            },
            [PDP_HEADER_TAB_LABEL.APPLICATIONS]: {
                tabPath: `${base}/applications?propertyId=${listing.propertyId}&itc=ZRM_PDP_Applications_Click`,
            },
            [PDP_HEADER_TAB_LABEL.LEASES]: {
                tabComponent: this.renderTabPanelComponent(PDP_HEADER_TAB_LABEL.LEASES),
                tabPath: `${base}/leasing/property/${listing.propertyId}`,
            },
            [PDP_HEADER_TAB_LABEL.PAYMENTS]: {
                tabPath: `${base}/payments/properties/${listing.propertyId}?itc=ZRM_PDP_Payments_Click`,
            },
            [PDP_HEADER_TAB_LABEL.ANALYTICS]: {
                tabComponent: this.renderTabPanelComponent(PDP_HEADER_TAB_LABEL.ANALYTICS),
            },
            [PDP_HEADER_TAB_LABEL.REVIEWS]: {
                tabComponent: this.renderTabPanelComponent(PDP_HEADER_TAB_LABEL.REVIEWS),
            },
        };

        const hideTabList = isEditListingFocused;

        // There is legacy code that maps the default listing response to other properties
        // ex:
        //   propertyPhotos > photos
        //   name > listingTitle

        // remap listing back to default response properties
        const listingCopy = cloneDeep(listing);

        // delete current properties
        delete listingCopy.listingTitle;
        delete listingCopy.photos;

        // assign default properties
        listingCopy.name = listing.listingTitle;
        listingCopy.propertyPhotos = listing.photos;

        return (
            <div
                className={cx('PDPHeader-Container', {
                    'PDP-page-native': isInMobileWebView,
                })}
            >
                <PDPHeader
                    brand={brand}
                    tabConfig={tabConfig}
                    hideTabList={hideTabList}
                    isInMobileWebView={isInMobileWebView}
                    listing={listingCopy}
                    pathname={location.pathname}
                    signMapUrl={this.signUrl}
                    handleEditClick={this.showAddPropertyModal}
                    handleBreadcrumbClick={this.handleBreadcrumbClick}
                />
            </div>
        );
    }

    renderPreLoader() {
        return <SpinnerContainer />;
    }

    render() {
        const { modalAddProperty } = this.props;
        const { behavior = 'Edit', isOpen } = modalAddProperty;
        const { isLoading, skipEditStage } = this.state;

        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <Container className="PropertyDetailsPage" fluidMobile={true}>
                    <Spacer paddingBottom="lg">
                        {!isLoading && (
                            <AddPropertyModal
                                isOpen={isOpen}
                                behavior={behavior}
                                skipEditStage={skipEditStage}
                            // TODO: destinationTab prop will need to be set for incoming traffic from apps/payments,
                            // e.g. via a query
                            />
                        )}
                        {isLoading ? this.renderPreLoader() : this.renderLoadComplete()}
                    </Spacer>
                </Container>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = (state, ownProps) => ({
    currentProperty: PropertiesSelectors.makeCurrentProperty(state),
    location: RoutingSelectors.makeLocation(state),
    modalAddProperty: ModalSelectors.makeAddPropertyModal(state),
    propertyId: ownProps.match.params.propertyId,
    router: RoutingSelectors.makeRouting(state),
    search: RoutingSelectors.makeSearch(state),
    prevPath: AppSelectors.makePrevPath(state),
    propertiesFilter: PropertiesSelectors.makePropertiesFilters(state),
});

export { PropertyDetailsPage };
export default withErrorBoundary(connect(mapStateToProps)(PropertyDetailsPage));
